import { watchImmediate } from "@vueuse/core";
import { computed, toValue } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { toSystemColorVariable } from "#ce/utils/color";
import { useModifiers } from "./useModifiers";

/**
 * Applies a color to a CSS property.
 * @param {string} color - The color to apply to the CSS property.
 * @param {HTMLElement} host - The host element.
 * @param {string} cssProperty - The CSS property to apply the color to.
 */
function applyColorProperty(color, host, cssProperty) {
  if (!host) {
    return;
  }

  if (color === undefined || color === null || color === "") {
    host.style.removeProperty(`--${cssProperty}`);
    return;
  }

  host.style.setProperty(`--${cssProperty}`, toSystemColorVariable(color));
}

/**
 * Sets a color css property on the host element.
 * @param {import('vue').MaybeRefOrGetter<string>} color - The color value (a ref, computed, or getter).
 * @param {object} options - Optional options.
 * @param {string} options.cssProperty - The CSS property to apply the color to. (default: "color")
 *
 * @example
 * useColorToCssProperty(() => props.color, { cssProperty: "fill" });
 *
 * will set the custom property on the host element: host.style.setProperty("--fill", "var(--sp-sys-color-primary)")
 */
export function useColorToCssProperty(color, options) {
  const host = useHostElement();
  const { cssProperty = "color" } = options ?? {};

  watchImmediate(
    () => toValue(color),
    (colorValue) => applyColorProperty(colorValue, host, cssProperty),
    options,
  );
}

/**
 * Applies a color to a CSS property.
 * @param {object} props - The props object.
 * @returns {object} The color modifiers.
 *
 * @example
 * const { colorModifiers } = useColor({ color: "primary" });
 * colorModifiers.value // "--color-primary)"
 */
export function useColor(props) {
  const { modifiers } = useModifiers(props, "color");
  const colorModifiers = computed(() => modifiers.value);

  return {
    colorModifiers,
  };
}
